import React, { useState, useCallback } from 'react';
import type { ContactFormData } from '../api/submitContactForm';
import './ContactForm.css';

export type ContactFormVariant = 'main' | 'i3';

export interface ContactFormProps {
  variant?: ContactFormVariant;
  onSubmit: (data: ContactFormData) => Promise<{ success: boolean; message: string }>;
  onSuccess?: () => void;
  subjectOptions?: { value: string; label: string }[];
  /** I-3: checkboxes for offerings (uses interestedServices on submit) */
  serviceOptions?: { value: string; label: string }[];
  servicesFieldLabel?: string;
  showContactInfo?: boolean;
  contactInfo?: {
    phone?: string;
    email?: string;
    location?: string;
  };
  className?: string;
}

const DEFAULT_SUBJECT_OPTIONS = [
  { value: 'p5-design', label: 'P5 Design' },
  { value: 'custom-development', label: 'Custom Development' },
  { value: 'process-automation', label: 'Process Automation' },
  { value: 'ai-analytics', label: 'AI & Analytics' },
  { value: 'cloud-solutions', label: 'Cloud Solutions' },
];

const INITIAL_FORM: ContactFormData = {
  name: '',
  email: '',
  phone: '',
  company: '',
  subject: '',
  message: '',
  interestedServices: '',
};

const ContactForm: React.FC<ContactFormProps> = ({
  variant = 'main',
  onSubmit,
  onSuccess,
  subjectOptions = DEFAULT_SUBJECT_OPTIONS,
  serviceOptions,
  servicesFieldLabel = 'What services are you interested in?',
  showContactInfo = true,
  contactInfo = {
    phone: '(972) 830-2268',
    email: 'info@p5techsolutions.com',
    location: 'Dallas, Texas',
  },
  className = '',
}) => {
  const [form, setForm] = useState<ContactFormData>(INITIAL_FORM);
  const [serviceSelections, setServiceSelections] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = useCallback((data: ContactFormData): Record<string, string> => {
    const next: Record<string, string> = {};
    if (!data.name.trim()) next.name = 'Name is required';
    if (!data.email.trim()) next.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) next.email = 'Please enter a valid email';
    if (variant === 'main' && !data.subject) next.subject = 'Please select a subject';
    if (!data.message.trim()) next.message = 'Message is required';
    return next;
  }, [variant]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setTouched((prev) => ({ ...prev, [e.target.name]: true }));
    const nextErrors = validate({ ...form, [e.target.name]: e.target.value });
    setErrors((prev) => ({ ...prev, [e.target.name]: nextErrors[e.target.name] || '' }));
  };

  const toggleService = (value: string) => {
    setServiceSelections((prev) => ({ ...prev, [value]: !prev[value] }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const interested = serviceOptions?.length
      ? Object.entries(serviceSelections)
          .filter(([, on]) => on)
          .map(([id]) => id)
          .join(', ')
      : '';
    const payload: ContactFormData = {
      ...form,
      interestedServices: interested,
    };
    const nextErrors = validate(payload);
    setErrors(nextErrors);
    setTouched({ name: true, email: true, subject: true, message: true });
    if (Object.keys(nextErrors).length > 0) return;

    setSubmitting(true);
    try {
      const result = await onSubmit(payload);
      setSubmitting(false);
      if (result.success) {
        setSuccess(true);
        setForm(INITIAL_FORM);
        setServiceSelections({});
        setTouched({});
        setErrors({});
        onSuccess?.();
      } else {
        setErrors({ submit: result.message });
      }
    } catch {
      setSubmitting(false);
      setErrors({ submit: 'Something went wrong. Please try again.' });
    }
  };

  const formClass = `contact-form-modern contact-form-variant-${variant} ${className}`.trim();

  if (success) {
    return (
      <div className={`${formClass} contact-form-success-wrap`}>
        <div className="contact-form-success" role="status">
          <span className="contact-form-success-icon" aria-hidden>
            <i className="fas fa-check-circle" />
          </span>
          <h3>Thank you</h3>
          <p>We&apos;ll get back to you soon.</p>
        </div>
      </div>
    );
  }

  return (
    <form className={formClass} onSubmit={handleSubmit} noValidate>
      {errors.submit && (
        <div className="contact-form-error-banner" role="alert">
          {errors.submit}
        </div>
      )}

      <div className="contact-form-grid">
        {showContactInfo && (contactInfo.phone || contactInfo.email || contactInfo.location) && (
          <div className="contact-form-info">
            <h3 className="contact-form-info-title">Get in touch</h3>
            <p className="contact-form-info-lead">Fill out the form and we&apos;ll respond as soon as we can.</p>
            <ul className="contact-form-details">
              {contactInfo.phone && (
                <li>
                  <i className="fas fa-phone" aria-hidden />
                  <span>{contactInfo.phone}</span>
                </li>
              )}
              {contactInfo.email && (
                <li>
                  <i className="fas fa-envelope" aria-hidden />
                  <a href={`mailto:${contactInfo.email}`}>{contactInfo.email}</a>
                </li>
              )}
              {contactInfo.location && (
                <li>
                  <i className="fas fa-map-marker-alt" aria-hidden />
                  <span>{contactInfo.location}</span>
                </li>
              )}
            </ul>
          </div>
        )}

        <div className="contact-form-fields">
          <div className="contact-form-row contact-form-row-split">
            <div className="contact-form-group">
              <label htmlFor="contact-name">Name</label>
              <input
                id="contact-name"
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                onBlur={handleBlur}
                required
                autoComplete="name"
                className={touched.name && errors.name ? 'contact-form-input-invalid' : ''}
                aria-invalid={!!errors.name}
                aria-describedby={errors.name ? 'contact-name-err' : undefined}
              />
              {touched.name && errors.name && (
                <span id="contact-name-err" className="contact-form-error" role="alert">{errors.name}</span>
              )}
            </div>
            <div className="contact-form-group">
              <label htmlFor="contact-email">Email</label>
              <input
                id="contact-email"
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                onBlur={handleBlur}
                required
                autoComplete="email"
                className={touched.email && errors.email ? 'contact-form-input-invalid' : ''}
                aria-invalid={!!errors.email}
                aria-describedby={errors.email ? 'contact-email-err' : undefined}
              />
              {touched.email && errors.email && (
                <span id="contact-email-err" className="contact-form-error" role="alert">{errors.email}</span>
              )}
            </div>
          </div>

          <div className="contact-form-row contact-form-row-split">
            <div className="contact-form-group">
              <label htmlFor="contact-phone">Phone</label>
              <input
                id="contact-phone"
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                onBlur={handleBlur}
                autoComplete="tel"
                placeholder="(555) 123-4567"
              />
            </div>
            <div className="contact-form-group">
              <label htmlFor="contact-company">Company</label>
              <input
                id="contact-company"
                type="text"
                name="company"
                value={form.company}
                onChange={handleChange}
                onBlur={handleBlur}
                autoComplete="organization"
              />
            </div>
          </div>

          {variant === 'i3' && serviceOptions && serviceOptions.length > 0 && (
            <div className="contact-form-group contact-form-services-wrap">
              <label id="contact-services-label">{servicesFieldLabel}</label>
              <p className="contact-form-services-hint" id="contact-services-hint">
                Select any that apply.
              </p>
              <div
                className="contact-form-services-box"
                role="group"
                aria-labelledby="contact-services-label"
                aria-describedby="contact-services-hint"
              >
                <ul className="contact-form-services-list">
                  {serviceOptions.map((opt) => (
                    <li key={opt.value}>
                      <label className="contact-form-service-label">
                        <input
                          type="checkbox"
                          name={`service-${opt.value}`}
                          checked={!!serviceSelections[opt.value]}
                          onChange={() => toggleService(opt.value)}
                        />
                        <span>{opt.label}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {variant === 'main' && subjectOptions.length > 0 && (
            <div className="contact-form-group">
              <label htmlFor="contact-subject">Subject</label>
              <select
                id="contact-subject"
                name="subject"
                value={form.subject}
                onChange={handleChange}
                onBlur={handleBlur}
                required
                className={touched.subject && errors.subject ? 'contact-form-input-invalid' : ''}
                aria-invalid={!!errors.subject}
                aria-describedby={errors.subject ? 'contact-subject-err' : undefined}
              >
                <option value="">Select a subject</option>
                {subjectOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              {touched.subject && errors.subject && (
                <span id="contact-subject-err" className="contact-form-error" role="alert">{errors.subject}</span>
              )}
            </div>
          )}

          <div className="contact-form-group">
            <label htmlFor="contact-message">Message</label>
            <textarea
              id="contact-message"
              name="message"
              rows={5}
              value={form.message}
              onChange={handleChange}
              onBlur={handleBlur}
              required
              placeholder="How can we help?"
              className={touched.message && errors.message ? 'contact-form-input-invalid' : ''}
              aria-invalid={!!errors.message}
              aria-describedby={errors.message ? 'contact-message-err' : undefined}
            />
            {touched.message && errors.message && (
              <span id="contact-message-err" className="contact-form-error" role="alert">{errors.message}</span>
            )}
          </div>

          <button
            type="submit"
            className="contact-form-submit"
            disabled={submitting}
            aria-busy={submitting}
          >
            {submitting ? (
              <>
                <i className="fas fa-spinner fa-spin" aria-hidden />
                Sending…
              </>
            ) : (
              'Send message'
            )}
          </button>
        </div>
      </div>
    </form>
  );
};

export default ContactForm;
